import { supabase } from '../lib/supabase';

const BUCKET_NAME = 'ImmoIQ';


/**
 * Storage service for managing property images in ImmoIQ bucket
 */
class StorageService {
  /**
   * Upload a file to the ImmoIQ bucket
   * @param {File} file - The file to upload
   * @param {string} userId - The user ID (for folder organization)
   * @param {string} folder - Optional subfolder (e.g., 'properties', 'analysis', 'documents')
   * @param {boolean} allowDocuments - If true, allows PDF and document files in addition to images
   * @returns {Promise<{success: boolean, filePath?: string, error?: string}>}
   */

  
  async uploadImage(file, userId, folder = 'properties', allowDocuments = false) {
    try {
      if (!file) {
        throw new Error('No file provided');
      }

      if (!userId) {
        throw new Error('User ID is required');
      }

      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const validDocumentTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const validTypes = allowDocuments
        ? [...validImageTypes, ...validDocumentTypes]
        : validImageTypes;

      if (!validTypes?.includes(file?.type)) {
        const allowedTypes = allowDocuments ? 'JPEG, PNG, WebP, PDF, DOC, DOCX' : 'JPEG, PNG, and WebP images';
        throw new Error(`Invalid file type. Only ${allowedTypes} are allowed.`);
      }

      // Validate file size (10MB limit for documents, 5MB for images)
      const maxSize = allowDocuments ? 10 * 1024 * 1024 : 5 * 1024 * 1024; // 10MB or 5MB
      if (file?.size > maxSize) {
        throw new Error(`File size exceeds ${allowDocuments ? '10MB' : '5MB'} limit.`);
      }

      // Generate unique filename
      const timestamp = Date.now();
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${timestamp}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${userId}/${folder}/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase?.storage?.from(BUCKET_NAME)?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

      if (error) {
        throw error;
      }

      return {
        success: true,
        filePath: data?.path
      };
    } catch (error) {
      console.error('Upload error:', error);
      return {
        success: false,
        error: error?.message || 'Failed to upload image'
      };
    }
  }

  /**
   * ✅ NEW: Download an image via your Edge Function proxy, convert to File, then upload
   * This makes it behave exactly like a manual upload (same validation + same bucket/path rules).
   *
   * @param {string} originalImageUrl - The original image URL (Centris, etc.)
   * @param {string} userId - The user ID (for folder organization)
   * @param {string} folder - Optional subfolder (default: 'properties')
   * @param {object} options
   * @param {string} options.edgeFunctionName - Supabase Edge Function name (default: 'image-proxy')
   * @param {number} options.minBytes - Reject tiny fallback images (default: 200 bytes)
   * @param {boolean} options.allowDocuments - Keep same signature style (default: false)
   * @returns {Promise<{success: boolean, filePath?: string, error?: string, contentType?: string, source?: string}>}
   */
  async uploadImageFromEdgeProxy(originalImageUrl, userId, folder = 'properties', options = {}) {
    const {
      edgeFunctionName = 'image-proxy',
      minBytes = 200,
      allowDocuments = false
    } = options || {};
  
    try {
      if (!originalImageUrl) throw new Error('Image URL is required');
      if (!userId) throw new Error('User ID is required');
  
      const supabaseUrl = supabase?.supabaseUrl || supabase?._supabaseUrl || null;
      if (!supabaseUrl) throw new Error('Supabase URL not available in client');
  
      console.log('[storage.uploadImageFromEdgeProxy] imageUrl:', originalImageUrl);
      console.log('[storage.uploadImageFromEdgeProxy] edgeFunctionName:', edgeFunctionName);
  
      const proxyUrl =
        `${supabaseUrl}/functions/v1/${edgeFunctionName}?url=${encodeURIComponent(originalImageUrl)}`;
  
      console.log('[storage.uploadImageFromEdgeProxy] invoking:', proxyUrl);
  
      // ✅ IMPORTANT: send auth header so the edge function isn't randomly blocked
      // (and so your function can log user context if you want later)
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
  
      const resp = await fetch(proxyUrl, {
        method: 'GET',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined
      });
  
      console.log('[storage.uploadImageFromEdgeProxy] edge response status:', resp.status);
      console.log('[storage.uploadImageFromEdgeProxy] content-type:', resp.headers.get('content-type'));
  
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        console.error('[storage.uploadImageFromEdgeProxy] edge error body:', txt?.slice?.(0, 500));
        throw new Error(`Proxy fetch failed (${resp.status})`);
      }
  
      const contentType = (resp.headers.get('content-type') || 'image/jpeg').toLowerCase();
      const blob = await resp.blob();
  
      if (!blob || blob.size < minBytes) {
        throw new Error(`Proxy returned a tiny fallback image (${blob?.size || 0} bytes)`);
      }
  
      const ext =
        contentType.includes('png') ? 'png' :
        contentType.includes('webp') ? 'webp' :
        contentType.includes('jpeg') || contentType.includes('jpg') ? 'jpg' :
        'jpg';
  
      const safeName = `proxy-${Date.now()}.${ext}`;
  
      // ✅ Some browsers can be picky, ensure file.type is a "real" image type
      const safeType =
        contentType.includes('png') ? 'image/png' :
        contentType.includes('webp') ? 'image/webp' :
        'image/jpeg';
  
      const file = new File([blob], safeName, { type: safeType });
  
      const up = await this.uploadImage(file, userId, folder, allowDocuments);
      if (!up?.success) throw new Error(up?.error || 'Failed to upload image');
  
      console.log('[storage.uploadImageFromEdgeProxy] uploaded filePath:', up.filePath);
  
      return {
        success: true,
        filePath: up.filePath,
        contentType: safeType,
        source: 'edge-proxy'
      };
    } catch (error) {
      console.error('uploadImageFromEdgeProxy error:', error);
      return { success: false, error: error?.message || 'Failed to upload image from proxy' };
    }
  }
  
  

  /**
   * Upload multiple files to the ImmoIQ bucket
   * @param {File[]} files - Array of files to upload
   * @param {string} userId - The user ID
   * @param {string} folder - Optional subfolder
   * @returns {Promise<{success: boolean, filePaths?: string[], errors?: string[]}>}
   */
  async uploadMultipleImages(files, userId, folder = 'properties') {
    try {
      const uploadPromises = files?.map((file) => this.uploadImage(file, userId, folder));
      const results = await Promise.all(uploadPromises);

      const successfulUploads = results?.filter((r) => r?.success);
      const failedUploads = results?.filter((r) => !r?.success);

      return {
        success: successfulUploads?.length > 0,
        filePaths: successfulUploads?.map((r) => r?.filePath),
        errors: failedUploads?.map((r) => r?.error)
      };
    } catch (error) {
      console.error('Multiple upload error:', error);
      return {
        success: false,
        errors: [error?.message || 'Failed to upload images']
      };
    }
  }

  /**
   * Get a signed URL for a private file (valid for 1 hour)
   * @param {string} filePath - The file path in the bucket
   * @returns {Promise<{success: boolean, url?: string, error?: string}>}
   */
  async getSignedUrl(filePath) {
    try {
      if (!filePath) {
        throw new Error('File path is required');
      }

      const { data, error } = await supabase?.storage?.from(BUCKET_NAME)?.createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) {
        throw error;
      }

      return {
        success: true,
        url: data?.signedUrl
      };
    } catch (error) {
      console.error('Get signed URL error:', error);
      return {
        success: false,
        error: error?.message || 'Failed to get signed URL'
      };
    }
  }

  /**
   * Get signed URLs for multiple files
   * @param {string[]} filePaths - Array of file paths
   * @returns {Promise<{success: boolean, urls?: string[], errors?: string[]}>}
   */
  async getMultipleSignedUrls(filePaths) {
    try {
      const urlPromises = filePaths?.map((path) => this.getSignedUrl(path));
      const results = await Promise.all(urlPromises);

      const successfulUrls = results?.filter((r) => r?.success);
      const failedUrls = results?.filter((r) => !r?.success);

      return {
        success: successfulUrls?.length > 0,
        urls: successfulUrls?.map((r) => r?.url),
        errors: failedUrls?.map((r) => r?.error)
      };
    } catch (error) {
      console.error('Multiple signed URLs error:', error);
      return {
        success: false,
        errors: [error?.message || 'Failed to get signed URLs']
      };
    }
  }

  /**
   * Delete a file from the ImmoIQ bucket
   * @param {string} filePath - The file path to delete
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteImage(filePath) {
    try {
      if (!filePath) {
        throw new Error('File path is required');
      }

      const { error } = await supabase?.storage?.from(BUCKET_NAME)?.remove([filePath]);

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Delete error:', error);
      return {
        success: false,
        error: error?.message || 'Failed to delete image'
      };
    }
  }

  /**
   * Delete multiple files from the ImmoIQ bucket
   * @param {string[]} filePaths - Array of file paths to delete
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteMultipleImages(filePaths) {
    try {
      if (!filePaths || filePaths?.length === 0) {
        throw new Error('File paths are required');
      }

      const { error } = await supabase?.storage?.from(BUCKET_NAME)?.remove(filePaths);

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Delete multiple error:', error);
      return {
        success: false,
        error: error?.message || 'Failed to delete images'
      };
    }
  }

  /**
   * List files in a user's folder
   * @param {string} userId - The user ID
   * @param {string} folder - Optional subfolder
   * @returns {Promise<{success: boolean, files?: Array, error?: string}>}
   */
  async listUserImages(userId, folder = 'properties') {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const folderPath = `${userId}/${folder}`;

      const { data, error } = await supabase?.storage?.from(BUCKET_NAME)?.list(folderPath, {
        limit: 100,
        offset: 0,
        sortBy: { column: 'created_at', order: 'desc' }
      });

      if (error) {
        throw error;
      }

      return {
        success: true,
        files: data
      };
    } catch (error) {
      console.error('List files error:', error);
      return {
        success: false,
        error: error?.message || 'Failed to list images'
      };
    }
  }
}

export default new StorageService();
